import { DialogLine, DialogSpeaker } from './VnDialog';
import { FixedEventDef, FixedMedia, FixedStep, PlotBeatEffect, PlotMessage, ScriptDef } from '../data/types';
import { GameData } from '../data/GameData';
import { GameState } from '../systems/GameState';
import { fanSystem } from '../systems/FanSystem';
import { beatEffects, plotNpcName } from '../systems/PlotSystem';
import { effectLabel } from './ui';
import { BUZZ_FALLBACK, buzzOfFame } from './buzzReads';

/**
 * 剧情演出通道（2026-10-13 形状 C 收口，茄子定「只做 VN 即时演出，回看页等剧情多了再谈」）
 *
 * 把收件箱条目翻成 VN 台词行：一「拍」＝一段台词（末行可挂软分支选项），
 * 播出＝交付，交付那一刻由调用方调 `PlotSystem.deliver()` 做结算（flag/结识/首拍效果）。
 * 不许本模块不碰任何数值与状态，只读数、组行——与偶遇那条「掷点函数不许驱动演出」的整改同规矩。
 *
 * 载体口径（2026-10-14 起接 media 弹窗）：
 *  - `encounter`／`message` → VN 通道（行级对白解析）
 *  - `media` → MediaPanel 弹窗（hotpost 热议帖 / news 新闻快讯）
 * 混载体序列里 media 拍交给 MediaPanel 演，encounter/message 拍走 VN，两通道交替推进。
 */

/** 旁白态（不带头像、无姓名）：与月执行页、公园同一形制 */
const NARRATOR: DialogSpeaker = { id: 'narration', name: '', noAvatar: true };

/** 同一 eventId 的非 encounter 拍每会话只喊一次（每周都演到同一拍会刷屏） */
const warned = new Set<string>();

/** 当前拍：有 steps 按指针取，无 steps 用事件本体兜底 */
export function plotStepOf(ev: FixedEventDef, msg: PlotMessage): FixedStep | null {
    return ev.steps && ev.steps.length ? ev.steps[msg.stepIndex] ?? null : null;
}

/** 这一拍是不是 media 载体（MediaPanel 弹窗演） */
export function isMediaBeat(ev: FixedEventDef, msg: PlotMessage): boolean {
    const step = plotStepOf(ev, msg);
    const medium = step ? step.medium : ev.medium;
    return medium === 'media';
}

/** 这一拍是不是 message 载体（2026-10-14 茄子定：人脉页聊天页的消息，月底 sweepChatDelivery 统一发送；
 *  演出档见到让位——不弹卡也不推进，跳过去演后面的条目。跳过≠推进，条目原地等月底） */
export function isChatBeat(ev: FixedEventDef, msg: PlotMessage): boolean {
    const step = plotStepOf(ev, msg);
    const medium = step ? step.medium : ev.medium;
    return medium === 'message';
}

/** 这一拍本通道演不演得了（VN 通道：encounter；message 已改走聊天页，不算 VN 可演） */
export function isPlayableBeat(ev: FixedEventDef, msg: PlotMessage): boolean {
    const step = plotStepOf(ev, msg);
    const medium = step ? step.medium : ev.medium;
    return medium === 'encounter';
}

/** 整条事件里有没有哪怕一拍可演（VN 或 media 都算可演） */
export function hasPlayableBeat(ev: FixedEventDef): boolean {
    return ev.medium !== 'media' || (ev.steps ?? []).some(s => s.medium !== 'media');
}

/**
 * 这条事件说的是哪部剧（插值口 `{script}` `{heat}` `{rating}` `{role}` 的主体）。
 * 四级回退：**钩子带入的剧**（`PlotMessage.scriptId`，通告钩子事件的首选来源，2026-10-24 通告事件批）→
 * 当拍自己挂的 scriptHeat → 事件级 → 最后回头扫各步——
 * 热爆那两帖（media 拍）本身不加热度、加值写在后面的 message 拍上，
 * 只查前两级会查无，实测帖文渲染成「开播三天，热度 0」（10-14 茄子令：怎么改合适就怎么改）。
 * 查无仍然要喊：正文里真用了占位符却反查不到剧＝数据错配，静默填 0 会被当成设定值。
 */
function subjectScriptOf(ev: FixedEventDef, step: FixedStep | null, used: boolean, msgScriptId?: string): ScriptDef | undefined {
    // 各级 scriptHeat 里挑**写了 scriptId 的那一条**（2026-11-03 起 scriptId 可缺省＝本次派发带入的剧，
    // 缺省条不能当静态挂剧依据，否则会把隔壁那部剧认成主体）
    const firstId = (list?: { scriptId?: string }[]) => list?.find(h => h.scriptId)?.scriptId;
    const id = msgScriptId
        ?? firstId(step?.scriptHeat)
        ?? firstId(ev.scriptHeat)
        ?? (ev.steps ?? []).flatMap(s => s.scriptHeat ?? []).find(h => h.scriptId)?.scriptId;
    const script = id ? GameData.SCRIPT_MAP[id] : undefined;
    if (!script && used && !warned.has(`subject:${ev.id}`)) {
        warned.add(`subject:${ev.id}`);
        console.warn(`[PlotStage] ${ev.id} 正文用了剧本式插值（{script}/{heat}/{rating}），但事件与各级 step 都没挂 scriptHeat，所以这几处只能填空/填 0（请给该事件任一拍补 scriptHeat）`);
    }
    return script;
}

/**
 * 剧本式插值表（media 拍与 encounter/message 拍共用同一份，2026-10-14 收口成一处）。
 * 热度读数＝`FanSystem.workHeatOf` 那把活尺（与热度榜、履历同口径，含剧宣封顶与客串加成），
 * 不是 `script.heat + plotHeatBonus` 那种裸加法——那会把玩家自己推上去的那本账漏掉，同一个数在两个页面读得不一样。
 * 剧与角色的来源：通告钩子事件读 `PlotMessage` 带入的 scriptId/roleIndex（2026-10-24 通告事件批），
 * 其余走 `subjectScriptOf` 的静态挂剧回退。
 */
function scriptSubsOf(ev: FixedEventDef, step: FixedStep | null, raw: string, msg?: PlotMessage): [string, string][] {
    const used = /\{(script|heat|rating|role)\}/.test(raw);
    const script = subjectScriptOf(ev, step, used, msg?.scriptId);
    const role = msg?.roleIndex !== undefined && script ? script.roles[msg.roleIndex] : undefined;
    return [
        ['{script}', script?.name ?? ''],
        ['{heat}', String(script ? fanSystem.workHeatOf(script) : 0)],
        ['{rating}', String(script?.baseScore ?? '')],
        // 角色名：本名优先，缺名回退角色定位（「《X》的女主角杀青」读得通）；
        // 定位当名会翻车的语境（颁奖夜「演 男主角 那半年」教训）别用 {role}，拆条目写死
        ['{role}', role ? (role.name || role.roleType) : ''],
    ];
}

/**
 * 取当前拍的 media 数据（含插值替换）。
 * 插值：`{script}` 剧名、`{heat}` 当前热度（活尺，见 `scriptSubsOf`）、`{rating}` 底分、`{role}` 角色名
 * （剧与角色的来源：钩子事件读 msg 带入的 scriptId/roleIndex，其余静态挂剧，见 `scriptSubsOf`），
 * 外加**玩家名两枚**（2026-10-22 用户令「正文可以带上玩家名字和她的话题，一些评论也带上」）：
 * `{player}` 昵称（优先，即公开的艺名）／`%玩家名%` 本名——与 VN 那条 `plotLinesFor` 同口径，
 * 标题、正文、评论、分档评论**四处都替**（黑帖正文与热评里点名的就是玩家）。
 * 出口必带 `buzz`（讨论/转发/点赞的量级锚）：表里填了用表里的，没填回退本事件所挂剧的
 * 当前热度，两者都无＝落 BUZZ_FALLBACK 并喊一次。MediaPanel 因此不必再判缺省。
 * 无 media 数据返回 null（数据错配态）。
 */
export function mediaBeatOf(ev: FixedEventDef, msg: PlotMessage): FixedMedia | null {
    const step = plotStepOf(ev, msg);
    const raw = step ? step.media : ev.media;
    if (!raw) return null;
    const subs: [string, string][] = [
        // 玩家名两枚（2026-10-22）：黑帖正文/热评要能被点名。口径与 VN 那条一致——
        // `{player}`＝昵称优先（公开艺名），`%玩家名%`＝本名。
        ['{player}', (GameState.getInstance().player.nickname ?? '').trim() || GameState.getInstance().player.name || '你'],
        ['%玩家名%', GameState.getInstance().player.name || '你'],
        ...scriptSubsOf(ev, step, `${raw.title ?? ''}${raw.body}`, msg),
    ];
    const apply = (s: string) => { for (const [k, v] of subs) s = s.split(k).join(v); return s; };
    // 舆论读数的量级锚（三级）：
    //   ① 表里填了 `buzz` ⇒ 作者说了算；
    //   ② **数值事件**（`playOn:'decisionEntry'`）⇒ 按当下**名气**换算（2026-10-22 用户令「名气越高话题度越高」）：
    //      同一个黑帖，天后被扒和小透明被扒不是一回事，所以话题度跟着人走、不写死在表里；
    //   ③ 挂剧的固定剧情 ⇒ 回退该剧当前热度（通告钩子事件＝msg 带入的剧，2026-10-24 起）；
    // 三者都没有（不带剧的八卦、盘点帖）才落兜底档并喊一次，别让它静默变成一个说不清的数。
    const anchor = subjectScriptOf(ev, step, false, msg.scriptId);
    const fameBuzz = ev.playOn === 'decisionEntry' ? buzzOfFame(GameState.getInstance().getFame()) : undefined;
    const buzz = raw.buzz ?? fameBuzz ?? (anchor ? fanSystem.workHeatOf(anchor) : undefined);
    if (buzz === undefined && !warned.has(`buzz:${ev.id}`)) {
        warned.add(`buzz:${ev.id}`);
        console.warn(`[PlotStage] ${ev.id} 的 media 既没填 buzz、事件各级也反查不到 scriptHeat，讨论/转发/点赞只能按 ${BUZZ_FALLBACK} 档兜底（不带剧的条目请在表里给一个话题度）`);
    }
    return {
        kind: raw.kind,
        style: raw.style,
        title: raw.title ? apply(raw.title) : raw.title,   // weibo 无标题：title 可缺省，别对 undefined 调 apply
        body: apply(raw.body),
        image: raw.image,
        author: raw.author ? apply(raw.author) : raw.author,  // weibo 博主名（营销号/后援会；后援会写 `{player}后援会`）
        authorAvatar: raw.authorAvatar,                    // 'player' ⇒ 卡片头像用玩家捏脸（后援会官方号）
        comments: raw.comments.map(apply),
        // 评论区按口碑分档（2026-10-22）：与 comments 一样过一遍剧本式插值，再原样交给 MediaPanel 取档
        commentsByRep: raw.commentsByRep?.map(b => ({ maxRep: b.maxRep, comments: b.comments.map(apply) })),
        buzz: buzz ?? BUZZ_FALLBACK,
    };
}

/**
 * 变动行文案（主体名 + 带符号增量）：负数首字符是 '-'，由 VnDialog 染成失败色，与行程/随机事件变动行同一形态。
 * 主体各自的叫法——属性与资源读 `effectLabel`（全站一把尺）、剧热度带书名号、好感只写「好感」
 * 不带人名（2026-10-15 茄子定：加好感的对话都是与当事人本人的聊天）；
 * 结识没有「量」，写「谁 已结识」（2026-10-10 茄子定）。
 * VN 与聊天页两颗气泡共用这一个格式化口（清单都是 `PlotSystem.beatEffects` 算出来的机器值）。
 */
export function formatBeatEffects(list: PlotBeatEffect[]): { label: string; delta: string }[] {
    const sign = (v: number) => `${v > 0 ? '+' : ''}${v}`;   // 负数自带 '-'
    return list.map(e => {
        switch (e.kind) {
            case 'meet': return { label: plotNpcName(e.id), delta: '已结识' };
            case 'favor': return { label: '好感', delta: sign(e.value) };   // 不带人名（2026-10-15 茄子定：加好感的对话都是与当事人本人的聊天）
            case 'scriptHeat': return { label: `《${GameData.SCRIPT_MAP[e.id]?.name ?? e.id}》热度`, delta: sign(e.value) };
            default: return { label: effectLabel(e.id), delta: sign(e.value) };
        }
    });
}

/** 这一拍的变动行（VN 通道：清单现算自事件表；聊天页那条走存根里落档的同一份清单）。
 *  `ctxScriptId`＝派发带入的剧，与 `deliver` 落账传的是同一个值——缺了它，scriptHeat 缺省 id 的那笔账在
 *  账本里有、在卡上少一行（10-15 定死的「配了什么数＝扣了什么账＝上屏什么行」）。 */
function beatChanges(ev: FixedEventDef, step: FixedStep | null, ctxScriptId?: string): { label: string; delta: string }[] {
    return formatBeatEffects(beatEffects(ev, step, ctxScriptId));
}

/** 说话人：有 NPC 挂 NPC（特殊 NPC 的社交全套在主表），assistant 读称呼常量，都没有＝旁白态 */
function speakerOf(npcId?: string): DialogSpeaker {
    if (!npcId) return NARRATOR;
    if (npcId !== 'assistant' && !GameData.NPC_MAP[npcId]) {
        console.warn(`[PlotStage] 剧情说话人 ${npcId} 在 NPC 主表查无，所以按旁白播（数据错配态）`);
        return NARRATOR;
    }
    // 脸由 `npcAvatar.withLineAvatars` 出（assistant 也在出图口径内），这里只给名字 + 首字兜底
    const name = plotNpcName(npcId);
    return { id: npcId, name, avatarChar: name.slice(0, 1) };
}

/**
 * 组一拍台词：正文按剧本式文本解析成多行 VN（支持交替多段制的行级细分）：
 *  - `名字：台词` → 该 NPC 的对白气泡（名字解析到主表则挂头像；解析不到＝具名无名角色）
 *  - `你：台词` → 主角气泡（isPlayer，姓名栏固定「你」）
 *  - `你：（…）` → 主角心理（同主角气泡）
 *  - 其余行＝旁白；整拍一行具名都没有＝老写法（一个人说完一整段），署名取该拍 `npcId`
 * 插值：`{player}` 玩家名（优先昵称）、`%玩家名%` 玩家本名（正式报名用）、`{script}` 剧名、`{role}` 角色名、`{heat}` 当前热度、`{rating}` 底分（暂）。
 * 高亮：`[[文字]]` 保留传递，VnDialog 读毕上色（打字期剥标记）。
 * 该拍有软分支就把选项挂在末行（组件规矩：选项只在整段最后一句上生效）。
 */
export function plotLinesFor(ev: FixedEventDef, msg: PlotMessage): DialogLine[] {
    const step = plotStepOf(ev, msg);
    const raw = (step ? step.description : ev.description) ?? '';
    if (!raw.trim()) {
        console.warn(`[PlotSystem] ${ev.id} 第 ${(step ? msg.stepIndex : 0) + 1} 拍正文为空，所以播一行占位旁白`);
        return [{ speaker: NARRATOR, text: '……' }];
    }

    const state = GameState.getInstance();
    const nick = (state.player.nickname ?? '').trim();
    const subs: [string, string][] = [
        ['{player}', nick || state.player.name || '你'],
        // 本名插值（2026-10-19）：正式报名/自我介绍这类场合不能报昵称（小名），
        // 用已登记的 `%玩家名%` 记号，读的是 `player.name` 本名。
        ['%玩家名%', state.player.name || '你'],
        ...scriptSubsOf(ev, step, raw, msg),
    ];

    const PLAYER: DialogSpeaker = { id: 'player', name: '你', isPlayer: true };
    const npcByName = (name: string): DialogSpeaker => {
        // 「小助理：」＝文案里的代称写法（Assistant.ts 定的口径：命名定稿前一律写这个），走同一张说话人尺
        if (name === plotNpcName('assistant')) return speakerOf('assistant');
        const npc = Object.values(GameData.NPC_MAP).find(n => n.name === name);
        if (npc) return { id: npc.id, name, avatarChar: name.slice(0, 1) };
        // ？？？：用当前拍的 npcId 查头像，姓名栏保持原文（玩家看到有脸但不知名的人）
        if (name === '？？？') {
            const fallbackNpcId = step ? step.npcId : ev.npcId;
            if (fallbackNpcId && GameData.NPC_MAP[fallbackNpcId]) {
                return { id: fallbackNpcId, name, avatarChar: GameData.NPC_MAP[fallbackNpcId].name.slice(0, 1) };
            }
        }
        // 路人/临时角色（对面的人/服装师等）：有姓名、无头像
        return { id: `who:${name}`, name, noAvatar: true };
    };

    const rows = raw.split('\n').map(l => {
        let s = l.trim();
        for (const [k, v] of subs) s = s.split(k).join(v);
        return s;
    }).filter(s => s.length > 0);
    // 整拍里一句具名/主角标记都没有＝叙述型拍（一个人说完一整段），署名取该拍 npcId；
    // 署名取该拍的 npcId；只要出现任一行 `名字：`/`你：`，未标记的行就是旁白（新格式：叙述与对白交替）
    const isNamed = (s: string) => /^(你：)/.test(s) || /^([^：]{1,12})：.+/.test(s);
    const beatSpeaker = rows.some(isNamed) ? null : speakerOf(step ? step.npcId : ev.npcId);

    const out: DialogLine[] = [];
    for (const s of rows) {
        if (s.startsWith('你：')) { out.push({ speaker: PLAYER, text: s.slice(2).trim() }); continue; }
        const m = s.match(/^([^：]{1,12})：(.+)$/);
        if (m) { out.push({ speaker: npcByName(m[1]), text: m[2] }); continue; }
        out.push({ speaker: beatSpeaker ?? NARRATOR, text: s });
    }
    if (out.length === 0) out.push({ speaker: NARRATOR, text: '……' });

    // 这一拍改的每一笔账，原样挂在末行下面（2026-10-15 茄子令「剧情改的数要当场看得见」）：
    // 清单来自 `PlotSystem.beatEffects`——与 `deliver` 落账用的是同一份、同一套 onlyRole 判据，
    // 所以「配了什么数」＝「扣了什么账」＝「上屏什么行」，导入新剧情不必再为显示补一行代码。
    // 调用方先交付再组行（结识已置 0、数值已到账），这里只把配置值念出来。
    const changes = beatChanges(ev, step, msg.scriptId);
    if (changes.length) out[out.length - 1].changes = changes;

    const choices = (step ? step.choices : ev.choices) ?? [];
    if (choices.length) {
        out[out.length - 1].choices = choices.map(c => ({ text: c.text }));
    }
    return out;
}

/** 非 encounter 拍的点名（一次/事件/拍位），调用方据此跳过该拍 */
export function warnUnplayable(eventId: string, stepIndex: number, medium: string): void {
    const key = `${eventId}#${stepIndex}`;
    if (warned.has(key)) return;
    warned.add(key);
    console.warn(`[PlotStage] ${eventId} 第 ${stepIndex + 1} 拍是 ${medium} 载体，本批 VN 通道不接，所以跳过该拍继续（收件箱无回看界面，茄子 10-13 定）`);
}
